// frontend/src/components/current-load/CurrentLoadMap.jsx

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  CalendarDays,
  Check,
  ChevronDown,
  ChevronRight,
  Clock3,
  Info,
  Maximize2,
  Minimize2,
  Navigation,
  Share2,
  Truck,
} from "lucide-react";

import {
  useTranslation,
} from "react-i18next";

import EditableShipmentRouteMap from "../maps/EditableShipmentRouteMap.jsx";

import {
  buildPendentLoadMapPoints,
  buildPendentLoadWorkspaceStops,
  getPendentLoadPointCoordinates,
} from "../../utils/pendentLoadShipmentAdapter.js";

import "../../styles/EditShipmentRequestPage.css";
import "../../styles/EditShipmentRequestPage.css";
import "../../styles/EditPendentLoadWorkspace.css";

const API_BASE_URL =
  "/api";

function getDispatcherAuthToken() {
  return String(
    localStorage.getItem(
      "token"
    ) ||
      sessionStorage.getItem(
        "token"
      ) ||
      localStorage.getItem(
        "authToken"
      ) ||
      sessionStorage.getItem(
        "authToken"
      ) ||
      localStorage.getItem(
        "accessToken"
      ) ||
      ""
  )
    .trim()
    .replace(
      /^Bearer\s+/i,
      ""
    )
    .replace(
      /^"|"$/g,
      ""
    );
}

function formatDistance(
  value,
  locale,
  t
) {
  const numberValue =
    Number(
      value
    );

  if (
    !Number.isFinite(
      numberValue
    ) ||
    numberValue <=
      0
  ) {
    return t(
      "common.calculating"
    );
  }

  return t(
    "workspace.distanceValue",
    {
      value:
        numberValue.toLocaleString(
          locale,
          {
            maximumFractionDigits:
              numberValue >=
              100
                ? 0
                : 1,
          }
        ),
    }
  );
}

function formatDuration(
  value,
  t
) {
  const seconds =
    Number(
      value
    );

  if (
    !Number.isFinite(
      seconds
    ) ||
    seconds <=
      0
  ) {
    return t(
      "common.calculating"
    );
  }

  const totalMinutes =
    Math.round(
      seconds /
        60
    );

  const hours =
    Math.floor(
      totalMinutes /
        60
    );

  const minutes =
    totalMinutes %
    60;

  if (
    hours <=
    0
  ) {
    return t(
      "workspace.durationMinutes",
      {
        minutes,
      }
    );
  }

  if (
    minutes ===
    0
  ) {
    return t(
      "workspace.durationHours",
      {
        hours,
      }
    );
  }

  return t(
    "workspace.durationHoursMinutes",
    {
      hours,
      minutes,
    }
  );
}

function formatCurrency(
  value,
  locale,
  t
) {
  const numberValue =
    Number(
      value
    );

  if (
    !Number.isFinite(
      numberValue
    )
  ) {
    return t(
      "common.calculating"
    );
  }

  return new Intl.NumberFormat(
    locale,
    {
      style:
        "currency",

      currency:
        "EUR",
    }
  ).format(
    numberValue
  );
}

function getRouteTollAmount(
  routeResult
) {
  const candidates = [
    routeResult
      ?.tollEstimate
      ?.amount,

    routeResult
      ?.tollEstimate
      ?.total,

    routeResult
      ?.tollAmount,

    routeResult
      ?.tollsAmount,

    routeResult
      ?.tollCost,
  ];

  for (
    const candidate
    of candidates
  ) {
    const parsed =
      Number(
        candidate
      );

    if (
      Number.isFinite(
        parsed
      )
    ) {
      return parsed;
    }
  }

  return null;
}

function formatLongDate(
  value,
  locale
) {
  const cleanValue =
    String(
      value ||
        ""
    ).trim();

  if (
    !cleanValue
  ) {
    return "—";
  }

  const date =
    new Date(
      `${cleanValue.slice(
        0,
        10
      )}T12:00:00`
    );

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return cleanValue;
  }

  return new Intl.DateTimeFormat(
    locale,
    {
      day:
        "2-digit",

      month:
        "short",

      year:
        "numeric",
    }
  ).format(
    date
  );
}

function getPointName(
  point,
  fallback
) {
  const candidates = [
    point?.location,
    point?.placeName,
    point?.city,
    point?.address,
  ];

  const found =
    candidates.find(
      (
        value
      ) =>
        String(
          value ||
            ""
        ).trim()
    );

  return String(
    found ||
      fallback
  ).trim();
}

function formatWeight(
  value,
  locale
) {
  const parsed =
    Number(
      String(
        value ??
          ""
      ).replace(
        ",",
        "."
      )
    );

  if (
    !Number.isFinite(
      parsed
    ) ||
    parsed <=
      0
  ) {
    return null;
  }

  /*
   * O EditLoad trabalha normalmente com o peso
   * da carga em kg. Aqui apenas o apresentamos.
   */
  if (
    parsed >=
    1000
  ) {
    return `${(
      parsed /
      1000
    ).toLocaleString(
      locale,
      {
        maximumFractionDigits:
          1,
      }
    )} t`;
  }

  return `${parsed.toLocaleString(
    locale,
    {
      maximumFractionDigits:
        0,
    }
  )} kg`;
}

export default function CurrentLoadMap({
  pickupPoints = [],
  deliveryPoints = [],
  weight,
  trailerType,
}) {
  const {
    t,
    i18n,
  } =
    useTranslation(
      "edit_load"
    );

  const locale =
    i18n.resolvedLanguage ||
    i18n.language ||
    "pt-PT";

  const mapSectionRef =
    useRef(
      null
    );

  const [
    routeResult,
    setRouteResult,
  ] =
    useState(
      null
    );

  const [
    routeMapError,
    setRouteMapError,
  ] =
    useState(
      ""
    );

  const [
    isMapFullscreen,
    setIsMapFullscreen,
  ] =
    useState(
      false
    );

  const workspaceStops =
    useMemo(
      () =>
        buildPendentLoadWorkspaceStops(
          pickupPoints,
          deliveryPoints
        ),
      [
        pickupPoints,
        deliveryPoints,
      ]
    );

  const routeMapPoints =
    useMemo(
      () =>
        buildPendentLoadMapPoints(
          workspaceStops
        ),
      [
        workspaceStops,
      ]
    );

  const pointsWithoutCoordinates =
    useMemo(
      () =>
        workspaceStops.filter(
          (
            stop
          ) =>
            !getPendentLoadPointCoordinates(
              stop
            )
        ).length,
      [
        workspaceStops,
      ]
    );

  const firstPickupPoint =
    Array.isArray(
      pickupPoints
    ) &&
    pickupPoints.length >
      0
      ? pickupPoints[
          0
        ]
      : null;

  const lastDeliveryPoint =
    Array.isArray(
      deliveryPoints
    ) &&
    deliveryPoints.length >
      0
      ? deliveryPoints[
          deliveryPoints.length -
            1
        ]
      : null;

  const firstPickupName =
    getPointName(
      firstPickupPoint,
      t(
        "workspace.origin"
      )
    );

  const lastDeliveryName =
    getPointName(
      lastDeliveryPoint,
      t(
        "workspace.destination"
      )
    );

  const finalDeliveryDate =
    formatLongDate(
      lastDeliveryPoint
        ?.date,
      locale
    );

  const finalDeliveryHour =
    String(
      lastDeliveryPoint
        ?.hour ||
        ""
    ).trim();

  const totalRouteStops =
    workspaceStops.length;

  const routeTollAmount =
    getRouteTollAmount(
      routeResult
    );

  const hasCalculatedRoute =
    Boolean(
      routeResult
        ?.geometry ||
      routeResult
        ?.polyline ||
      (
        Number.isFinite(
          Number(
            routeResult
              ?.distanceKm
          )
        ) &&
        Number(
          routeResult
            ?.distanceKm
        ) >
          0
      )
    );

  const routeHasTolls =
    Boolean(
      routeResult
        ?.hasTolls ||
      (
        routeTollAmount !==
          null &&
        routeTollAmount >
          0
      )
    );

  const formattedWeight =
    formatWeight(
      weight,
      locale
    );

  const cleanTrailerType =
    String(
      trailerType ||
        ""
    )
      .trim()
      .toUpperCase();

  const translatedTrailerType =
    cleanTrailerType
      ? t(
          `trailerType.options.${cleanTrailerType}`
        )
      : "";

  useEffect(
    () => {
      const handleFullscreenChange =
        () => {
          setIsMapFullscreen(
            Boolean(
              mapSectionRef.current &&
              document.fullscreenElement ===
                mapSectionRef.current
            )
          );
        };

      document.addEventListener(
        "fullscreenchange",
        handleFullscreenChange
      );

      handleFullscreenChange();

      return () => {
        document.removeEventListener(
          "fullscreenchange",
          handleFullscreenChange
        );
      };
    },
    []
  );

  const handleExpandMap =
    async () => {
      const mapSection =
        mapSectionRef.current;

      if (
        !mapSection
      ) {
        return;
      }

      try {
        if (
          document.fullscreenElement ===
          mapSection
        ) {
          if (
            document.exitFullscreen
          ) {
            await document.exitFullscreen();
          }

          return;
        }

        if (
          mapSection.requestFullscreen
        ) {
          await mapSection.requestFullscreen();
        }
      } catch (
        fullscreenError
      ) {
        console.warn(
          "[CurrentLoadMap] Could not toggle map fullscreen:",
          fullscreenError
        );
      }
    };

  const handleScrollToRoute =
    () => {
      document
        .querySelector(
          ".current-load-route-panel, .pendent-workspace-route-panel"
        )
        ?.scrollIntoView({
          behavior:
            "smooth",

          block:
            "start",
        });
    };

  return (
    <section
      ref={
        mapSectionRef
      }
      className={[
        "shipment-v2-map-section",
        "pendent-workspace-map-section",
        "current-load-map-section",

        isMapFullscreen
          ? "is-map-fullscreen"
          : "",
      ]
        .filter(
          Boolean
        )
        .join(
          " "
        )}
    >
      <div className="edit-request-route-map-toolbar">
        <div className="edit-request-route-map-toolbar__main">
          <span
            className="edit-request-route-map-toolbar__route-icon"
            aria-hidden="true"
          >
            <Share2
              size={
                23
              }
            />
          </span>

          <div className="edit-request-route-map-toolbar__distance">
            <strong>
              {formatDistance(
                routeResult
                  ?.distanceKm,
                locale,
                t
              )}
            </strong>
          </div>

          <span
            className="edit-request-route-map-toolbar__divider"
            aria-hidden="true"
          />

          <div className="edit-request-route-map-toolbar__metric">
            <strong>
              {formatDuration(
                routeResult
                  ?.durationSeconds,
                t
              )}
            </strong>

            <small>
              {t(
                "workspace.estimatedDuration"
              )}
            </small>
          </div>

          <span
            className="edit-request-route-map-toolbar__divider"
            aria-hidden="true"
          />

          <div className="edit-request-route-map-toolbar__metric edit-request-route-map-toolbar__metric--tolls">
            <strong>
              {!hasCalculatedRoute
                ? t(
                    "common.calculating"
                  )
                : routeHasTolls
                  ? routeTollAmount !==
                    null
                    ? formatCurrency(
                        routeTollAmount,
                        locale,
                        t
                      )
                    : t(
                        "workspace.withTolls"
                      )
                  : t(
                      "workspace.noTolls"
                    )}
            </strong>

            <small>
              {!hasCalculatedRoute
                ? t(
                    "workspace.calculatingTolls"
                  )
                : routeHasTolls
                  ? t(
                      "workspace.estimatedTolls"
                    )
                  : t(
                      "workspace.routeWithoutTolls"
                    )}
            </small>
          </div>
        </div>

        <div className="edit-request-route-map-toolbar__right">
          <button
            type="button"
            className="edit-request-vehicle-profile-button"
          >
            <span className="edit-request-vehicle-profile-button__icon">
              <Truck
                size={
                  19
                }
              />
            </span>

            <span className="edit-request-vehicle-profile-button__copy">
              <strong>
                {t(
                  "workspace.vehicleProfile.title"
                )}
              </strong>

              <small>
                {translatedTrailerType
                  ? `${translatedTrailerType}${
                      formattedWeight
                        ? ` · ${formattedWeight}`
                        : ""
                    }`
                  : formattedWeight
                    ? t(
                        "workspace.vehicleProfile.loadWithWeight",
                        {
                          weight:
                            formattedWeight,
                        }
                      )
                    : t(
                        "workspace.vehicleProfile.truck"
                      )}
              </small>
            </span>

            <ChevronDown
              className="edit-request-vehicle-profile-button__chevron"
              size={
                17
              }
            />
          </button>
        </div>
      </div>

      <div className="shipment-v2-eta-card">
        <div className="shipment-v2-eta-card__header">
          <span className="shipment-v2-eta-card__status-icon">
            <Check
              size={
                14
              }
            />
          </span>

          <strong>
            {t(
              "workspace.finalDeliveryExpected"
            )}
          </strong>
        </div>

        <div className="shipment-v2-eta-card__date-panel">
          <span className="shipment-v2-eta-card__date-icon">
            <CalendarDays
              size={
                18
              }
            />
          </span>

          <div className="shipment-v2-eta-card__date-copy">
            <span>
              {t(
                "workspace.date"
              )}
            </span>

            <strong>
              {finalDeliveryDate}
            </strong>

            {finalDeliveryHour && (
              <small>
                {finalDeliveryHour}
              </small>
            )}
          </div>
        </div>

        <div className="shipment-v2-eta-meta">
          <Clock3
            size={
              13
            }
          />

          <span>
            {t(
              "workspace.configuredStops",
              {
                count:
                  totalRouteStops,
              }
            )}
          </span>
        </div>

        <button
          type="button"
          onClick={
            handleScrollToRoute
          }
        >
          <span className="shipment-v2-eta-card__button-content">
            <Navigation
              size={
                14
              }
            />

            <strong>
              {t(
                "workspace.viewFullRoute"
              )}
            </strong>
          </span>

          <ChevronRight
            size={
              15
            }
          />
        </button>
      </div>

      <EditableShipmentRouteMap
        points={
          routeMapPoints
        }
        editable={
          false
        }
        showSearch={
          isMapFullscreen
        }
        avoidTolls={
          false
        }
        apiBaseUrl={
          API_BASE_URL
        }
        authToken={
          getDispatcherAuthToken()
        }
        showVehicleRestrictions={
          false
        }
        onRouteChange={
          setRouteResult
        }
        onError={
          (
            nextError
          ) => {
            setRouteMapError(
              nextError
                ? t(
                    "errors.routeMapFailed"
                  )
                : ""
            );
          }
        }
      />

      <div className="shipment-v2-map-actions">
        <button
          type="button"
          onClick={
            handleExpandMap
          }
        >
          <span className="shipment-v2-map-actions__content">
            {isMapFullscreen ? (
              <Minimize2
                size={
                  19
                }
              />
            ) : (
              <Maximize2
                size={
                  19
                }
              />
            )}

            <span>
              <strong>
                {isMapFullscreen
                  ? t(
                      "workspace.map.collapse"
                    )
                  : t(
                      "workspace.map.expand"
                    )}
              </strong>
            </span>
          </span>

          <ChevronRight
            size={
              20
            }
          />
        </button>
      </div>

      {routeMapError && (
        <div className="edit-pendent-load-map-feedback is-error">
          <Info
            size={
              14
            }
          />

          <span>
            {routeMapError}
          </span>
        </div>
      )}

      {pointsWithoutCoordinates >
        0 && (
        <div className="edit-pendent-load-map-feedback is-warning">
          <Info
            size={
              14
            }
          />

          <span>
            {t(
              "workspace.map.pointsWithoutCoordinates",
              {
                count:
                  pointsWithoutCoordinates,
              }
            )}
          </span>
        </div>
      )}

      {!routeMapError &&
        pointsWithoutCoordinates ===
          0 &&
        routeMapPoints.length >=
          2 && (
          <div className="edit-pendent-load-map-feedback is-success">
            <Check
              size={
                14
              }
            />

            <span>
              {firstPickupName} →{" "}
              {lastDeliveryName}
            </span>
          </div>
        )}
    </section>
  );
}